import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../api/client';
import WorkOrderDynamicForm from '../work-order/WorkOrderDynamicForm';
import {
  answersFromTemplate,
  completionStats,
  formatReportDate,
  startOfLocalDay,
  type WorkOrderFormTemplate,
  type WorkOrderReport,
  type WorkOrderReportAnswer
} from '../../lib/workOrderForm';
import '../../pages/admin/work-order-forms/work-order-forms.css';

type WorkOrderDetails = {
  _id: string;
  title?: string;
  instructions?: string;
  formTemplateId?: string;
  client?: { name?: string; address?: string; phone?: string; email?: string };
  documents?: Array<{ name: string; url: string }>;
  schedule?: { date?: string; startTime?: string; endTime?: string };
};

type Props = {
  workOrderId: string;
  workOrder: WorkOrderDetails | null;
  /** Day selected in Job Log — defaults to today. */
  reportDate?: Date;
  /** Hide duplicate job details when embedded in Job Log. */
  embedded?: boolean;
};

function sameReportDay(reportDateIso: string | undefined, day: Date): boolean {
  if (!reportDateIso) return false;
  const a = new Date(reportDateIso);
  const b = new Date(day);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return a.getTime() === b.getTime();
}

export default function TechnicianWorkOrderPanel({
  workOrderId,
  workOrder,
  reportDate,
  embedded = false
}: Props): React.JSX.Element {
  const [template, setTemplate] = useState<WorkOrderFormTemplate | null>(null);
  const [reports, setReports] = useState<WorkOrderReport[]>([]);
  const [activeReport, setActiveReport] = useState<WorkOrderReport | null>(null);
  const [answers, setAnswers] = useState<WorkOrderReportAnswer[]>([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const targetDay = useMemo(() => reportDate ?? new Date(), [reportDate]);
  const targetDayIso = useMemo(() => startOfLocalDay(targetDay), [targetDay]);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const reportRes = await apiRequest<{ success: boolean; data: WorkOrderReport[] }>(
        `/api/work-orders/${workOrderId}/reports`
      );
      setReports(reportRes.data || []);
      if (workOrder?.formTemplateId) {
        const tplRes = await apiRequest<{ success: boolean; data: WorkOrderFormTemplate }>(
          `/api/work-order-form-templates/${workOrder.formTemplateId}`
        );
        setTemplate(tplRes.data);
      } else {
        setTemplate(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load work order reports');
    } finally {
      setBusy(false);
    }
  }, [workOrder?.formTemplateId, workOrderId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setActiveReport(null);
    setAnswers([]);
  }, [workOrderId, targetDayIso]);

  const dayReport = useMemo(() => {
    return reports.find((r) => sameReportDay(r.reportDate, targetDay)) || null;
  }, [reports, targetDay]);

  useEffect(() => {
    if (activeReport) {
      setAnswers(activeReport.answers || []);
      setTemplate(activeReport.templateSnapshot);
      return;
    }
    if (dayReport) {
      setActiveReport(dayReport);
      setAnswers(dayReport.answers || []);
      setTemplate(dayReport.templateSnapshot);
      return;
    }
    if (template && !activeReport) {
      setAnswers(answersFromTemplate(template));
    }
  }, [activeReport, dayReport, template]);

  const progress = useMemo(() => {
    if (!template) return { complete: 0, total: 0 };
    return completionStats(template, answers);
  }, [template, answers]);

  async function save(submit: boolean) {
    if (!template) return;
    setSaving(true);
    setError(null);
    try {
      if (activeReport) {
        const res = await apiRequest<{ success: boolean; data: WorkOrderReport }>(
          `/api/work-orders/${workOrderId}/reports/${activeReport._id}`,
          { method: 'PATCH', json: { answers, submit } }
        );
        setActiveReport(res.data);
        await load();
      } else {
        const res = await apiRequest<{ success: boolean; data: WorkOrderReport }>(
          `/api/work-orders/${workOrderId}/reports`,
          { method: 'POST', json: { answers, submit, reportDate: targetDayIso } }
        );
        setActiveReport(res.data);
        await load();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (embedded && !workOrder?.formTemplateId) return <></>;

  if (busy) return <p>Loading work order reports…</p>;

  const reportDayLabel = formatReportDate(targetDayIso);

  return (
    <div>
      {!embedded ? (
        <div className="wo-details-card">
          <h3>Job details</h3>
          <p><strong>{workOrder?.title || 'Assigned job'}</strong></p>
          {workOrder?.instructions ? <p>{workOrder.instructions}</p> : <p>No special instructions from admin.</p>}
          {workOrder?.client?.address ? <p>{workOrder.client.address}</p> : null}
          {workOrder?.client?.phone ? <p>Phone: {workOrder.client.phone}</p> : null}
          {workOrder?.schedule?.date ? (
            <p>
              Scheduled: {new Date(workOrder.schedule.date).toLocaleDateString()}
              {workOrder.schedule.startTime ? ` · ${workOrder.schedule.startTime}` : ''}
            </p>
          ) : null}
          {workOrder?.documents && workOrder.documents.length > 0 ? (
            <div>
              <p><strong>Documents</strong></p>
              {workOrder.documents.map((d) => (
                <p key={d.url}><a href={d.url} target="_blank" rel="noreferrer">{d.name}</a></p>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {!embedded && !workOrder?.formTemplateId ? (
        <div className="wo-details-card">
          <p>No work order form template is attached to this job yet. Ask an administrator to assign one.</p>
        </div>
      ) : null}

      {error ? <div className="banner banner-error">{error}</div> : null}

      {template ? (
        <>
          <div className="wo-progress">
            <p className="tech-sub">
              {reportDayLabel} · {progress.complete}/{progress.total} required fields complete
            </p>
            <div className="wo-progress-bar">
              <div
                className="wo-progress-fill"
                style={{ width: progress.total ? `${(progress.complete / progress.total) * 100}%` : '0%' }}
              />
            </div>
          </div>

          <div className="wo-report-list">
            {reports.map((r) => (
              <button
                key={r._id}
                type="button"
                className="wo-report-row"
                style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => setActiveReport(r)}
              >
                <span>{formatReportDate(r.reportDate)} · {r.status}</span>
                <span className="tech-badge ok">{r.status}</span>
              </button>
            ))}
          </div>

          {!dayReport && !activeReport ? (
            <button type="button" className="tech-btn tech-btn-primary" style={{ marginBottom: 12 }} onClick={() => setActiveReport(null)}>
              Start report for {reportDayLabel}
            </button>
          ) : null}

          <WorkOrderDynamicForm
            sections={template.sections || []}
            answers={answers}
            disabled={activeReport?.status === 'submitted'}
            onChange={setAnswers}
          />

          {activeReport?.status !== 'submitted' ? (
            <div className="wo-form-actions" style={{ marginTop: 16 }}>
              <button type="button" className="tech-btn tech-btn-ghost" disabled={saving} onClick={() => void save(false)}>
                Save progress
              </button>
              <button type="button" className="tech-btn tech-btn-primary" disabled={saving} onClick={() => void save(true)}>
                Submit report
              </button>
            </div>
          ) : (
            <p className="tech-sub">This report has been submitted.</p>
          )}
        </>
      ) : null}
    </div>
  );
}
